import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface CurrencyInfo {
  code: string;
  symbol: string;
  name: string;
  /** Approximate FX rate from 1 USD -> this currency. Static client-side display only. */
  rate: number;
}

// Static display-only conversion rates from USD. Checkout still occurs in the
// store's base currency (USD). These are approximate and used purely to give
// shoppers a familiar reference price.
export const CURRENCIES: CurrencyInfo[] = [
  { code: "CAD", symbol: "CA$", name: "Canadian Dollar", rate: 1.37 },
  { code: "USD", symbol: "$", name: "US Dollar", rate: 1 },
  { code: "EUR", symbol: "€", name: "Euro", rate: 0.93 },
  { code: "GBP", symbol: "£", name: "British Pound", rate: 0.79 },
  { code: "AUD", symbol: "A$", name: "Australian Dollar", rate: 1.52 },
  { code: "NZD", symbol: "NZ$", name: "New Zealand Dollar", rate: 1.66 },
];

interface CurrencyState {
  code: string;
  setCode: (code: string) => void;
}

export const useCurrencyStore = create<CurrencyState>()(
  persist(
    (set) => ({
      code: "USD",
      setCode: (code) => set({ code }),
    }),
    { name: "brimmob-currency" }
  )
);

export const getCurrency = (code: string): CurrencyInfo =>
  CURRENCIES.find((c) => c.code === code) ?? CURRENCIES[0];

/**
 * Format a price string/number originally expressed in `sourceCurrency`
 * (defaulting to USD), converting it to the user's selected display currency.
 * Conversion uses the static rates above and is for display only.
 */
export const formatConvertedPrice = (
  amount: string | number,
  sourceCurrency: string | undefined,
  displayCode: string
): string => {
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  if (!isFinite(num)) return "";
  const src = getCurrency(sourceCurrency || "USD");
  const dst = getCurrency(displayCode);
  // Convert source -> USD -> destination
  const inUsd = num / (src.rate || 1);
  const converted = inUsd * dst.rate;
  const decimals = ["JPY", "KRW", "VND", "IDR", "CLP", "COP", "HUF"].includes(dst.code) ? 0 : 2;
  const formatted = converted.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${dst.symbol}${formatted}`;
};